import { useCallback, useEffect, useState } from "react";
import { Button } from "@electro-planner/ui";
import {
  deleteRevision,
  getRevisionBytes,
  listRevisions,
  renameRevision,
  saveRevision,
  type RevisionRecord,
} from "./store.ts";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  getCurrentUpdateBytes: () => Uint8Array;
  onRestore: (bytes: Uint8Array) => void;
};

export function RevisionsDialog({
  open,
  onOpenChange,
  documentId,
  getCurrentUpdateBytes,
  onRestore,
}: Props) {
  const [revisions, setRevisions] = useState<RevisionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listRevisions(documentId);
      setRevisions(list);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const onSave = useCallback(async () => {
    const bytes = getCurrentUpdateBytes();
    await saveRevision(documentId, label, bytes);
    setLabel("");
    await refresh();
  }, [documentId, getCurrentUpdateBytes, label, refresh]);

  const onRestoreClick = useCallback(
    async (rec: RevisionRecord) => {
      const ok = window.confirm(
        `Återställ till "${rec.label}"?\n\nAktuell ritning ersätts. Du kan ångra (Ctrl+Z) direkt efteråt om det blev fel.`,
      );
      if (!ok) return;
      const bytes = await getRevisionBytes(documentId, rec.id);
      if (!bytes) return;
      onRestore(bytes);
      onOpenChange(false);
    },
    [documentId, onRestore, onOpenChange],
  );

  const onDelete = useCallback(
    async (rec: RevisionRecord) => {
      const ok = window.confirm(`Radera revision "${rec.label}"?`);
      if (!ok) return;
      await deleteRevision(documentId, rec.id);
      await refresh();
    },
    [documentId, refresh],
  );

  const onRename = useCallback(
    async (rec: RevisionRecord) => {
      const next = window.prompt("Nytt namn:", rec.label);
      if (next === null) return;
      await renameRevision(documentId, rec.id, next);
      await refresh();
    },
    [documentId, refresh],
  );

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Revisioner"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-[560px] max-w-[92vw] rounded-lg border border-[var(--color-border)] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <h2 className="text-base font-semibold">Revisioner</h2>
          <button
            type="button"
            aria-label="Stäng"
            onClick={() => onOpenChange(false)}
            className="rounded p-1 text-[var(--color-muted)] hover:bg-[var(--color-border)] hover:text-[var(--color-fg)]"
          >
            ×
          </button>
        </div>
        <div className="border-b border-[var(--color-border)] px-4 py-3">
          <label className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
            Spara aktuellt läge som ny revision
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`T.ex. "Innan kabelomdragning" (${new Date().toLocaleString("sv-SE")})`}
              className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-white px-2 py-1 text-sm outline-none focus:ring-2"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void onSave();
                }
              }}
            />
            <Button size="sm" onClick={onSave}>
              Spara
            </Button>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Lagras lokalt i browserns IndexedDB. Följer inte med mellan enheter
            — för det, använd Exportera backup.
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto px-4 py-2">
          {loading ? (
            <p className="py-4 text-center text-sm text-[var(--color-muted)]">
              Laddar…
            </p>
          ) : revisions.length === 0 ? (
            <p className="py-4 text-center text-sm text-[var(--color-muted)]">
              Inga revisioner sparade än.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {revisions.map((r) => (
                <li key={r.id} className="flex items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-[var(--color-muted)]">
                      {new Date(r.createdAt).toLocaleString("sv-SE")} ·{" "}
                      {formatBytes(r.sizeBytes)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRename(r)}
                    title="Byt namn"
                  >
                    Byt namn
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(r)}
                    title="Radera revision"
                  >
                    Radera
                  </Button>
                  <Button size="sm" onClick={() => onRestoreClick(r)}>
                    Återställ
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
